import { useState, useRef, useCallback, KeyboardEvent } from 'react'
import { Send, Paperclip, Smile } from 'lucide-react'
import { cn } from '@/utils/cn'

interface MessageInputProps {
  onSend: (content: string) => Promise<void>
  onTyping?: (isTyping: boolean) => void
  disabled?: boolean
  placeholder?: string
}

export function MessageInput({
  onSend,
  onTyping,
  disabled = false,
  placeholder = 'Digite uma mensagem...',
}: MessageInputProps) {
  const [value, setValue] = useState('')
  const [isSending, setIsSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleTyping = useCallback(() => {
    onTyping?.(true)
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => {
      onTyping?.(false)
    }, 2000)
  }, [onTyping])

  const adjustHeight = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    adjustHeight()
    if (e.target.value) handleTyping()
  }

  const handleSend = async () => {
    const trimmed = value.trim()
    if (!trimmed || isSending || disabled) return

    setIsSending(true)
    try {
      await onSend(trimmed)
      setValue('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      onTyping?.(false)
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    } catch {
      // Error handled upstream
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const canSend = value.trim().length > 0 && !isSending && !disabled

  return (
    <div
      className={cn(
        'flex items-end gap-2 rounded-[24px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.045)] px-3 py-2 shadow-[0_8px_28px_rgba(8,8,25,0.16)] backdrop-blur-sm',
      )}
    >
      {/* Attach button */}
      <button
        type="button"
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
          'hover:bg-[var(--glass-medium)] transition-colors duration-150',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        )}
        aria-label="Anexar arquivo"
        disabled={disabled}
      >
        <Paperclip size={18} />
      </button>

      {/* Emoji button */}
      <button
        type="button"
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
          'hover:bg-[var(--glass-medium)] transition-colors duration-150',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        )}
        aria-label="Emojis"
        disabled={disabled}
      >
        <Smile size={18} />
      </button>

      {/* Text area */}
      <div className="relative flex-1">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'w-full rounded-[18px] border border-transparent bg-[rgba(255,255,255,0.035)] px-4 py-3',
            'text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none',
            'focus:outline-none',
            'max-h-[120px] overflow-y-auto',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          style={{ minHeight: '46px' }}
          aria-label="Mensagem"
        />
      </div>

      {/* Send button */}
      <button
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'transition-all duration-150',
          canSend
            ? 'bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)] shadow-[0_2px_8px_rgba(var(--color-brand-rgb),0.3)]'
            : 'bg-[var(--glass-medium)] text-[var(--text-muted)] cursor-not-allowed',
        )}
        aria-label="Enviar mensagem"
      >
        {isSending ? (
          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <Send size={16} />
        )}
      </button>
    </div>
  )
}
